import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/doi-tac-module/lib/utils';
import { S3File } from '@/doi-tac-module/lib/types';

interface ImageGalleryLightboxProps {
    isOpen: boolean;
    onClose: () => void;
    images: (string | S3File)[];
    currentIndex: number;
    onIndexChange?: (index: number) => void;
}

export const ImageGalleryLightbox = ({
    isOpen,
    onClose,
    images,
    currentIndex: initialIndex,
    onIndexChange
}: ImageGalleryLightboxProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
        }
    }, [isOpen, initialIndex]);

    const getUrl = (img: string | S3File) => typeof img === 'string' ? img : img.url;

    const handlePrevious = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            onIndexChange?.(newIndex);
        }
    }, [currentIndex, onIndexChange]);

    const handleNext = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (currentIndex < images.length - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            onIndexChange?.(newIndex);
        }
    }, [currentIndex, images.length, onIndexChange]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!isOpen) return;
        if (e.key === 'ArrowLeft') handlePrevious();
        if (e.key === 'ArrowRight') handleNext();
        if (e.key === 'Escape') onClose();
    }, [isOpen, handlePrevious, handleNext, onClose]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!isOpen || !images.length) return null;

    const currentImage = images[currentIndex];

    // Download File Logic
    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        const url = getUrl(currentImage);
        // Simple download trigger
        const link = document.createElement('a');
        link.href = url;
        link.download = `image-${currentIndex + 1}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 m-0 border-none bg-white backdrop-blur-sm sm:rounded-none !rounded-none focus:outline-none flex flex-col items-center justify-center">
                <DialogHeader className="hidden">
                    <DialogTitle>View Image</DialogTitle>
                </DialogHeader>

                {/* Top Action Bar */}
                <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-white/90 to-transparent flex items-center justify-between px-6 z-50">
                    <div className="text-slate-500 font-bold text-sm tracking-widest uppercase">
                        {currentIndex + 1} / {images.length}
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full h-10 w-10 flex-shrink-0"
                            onClick={handleDownload}
                        >
                            <Download className="w-5 h-5" />
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="bg-slate-100 text-slate-600 hover:text-white hover:bg-red-500/80 rounded-full h-10 w-10 flex-shrink-0 transition-colors"
                            onClick={onClose}
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </div>

                {/* Image Container */}
                <div 
                    className="relative w-full h-[80vh] flex items-center justify-center p-4 sm:p-12 md:p-20 outline-none select-none transition-transform duration-300 ease-out"
                    onClick={onClose}
                >
                    <img 
                        src={getUrl(currentImage)} 
                        alt={`Gallery ${currentIndex + 1}`}
                        className="max-w-full max-h-full object-contain rounded-sm shadow-2xl shadow-slate-200 animate-in fade-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
                    />
                </div>

                {/* Navigation Arrows */}
                {currentIndex > 0 && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 bg-white/60 hover:bg-white/90 border border-slate-200 text-slate-800 rounded-full backdrop-blur-md transition-all hidden md:flex hover:scale-105 active:scale-95 shadow-lg"
                        onClick={handlePrevious}
                    >
                        <ChevronLeft className="w-8 h-8" />
                    </Button>
                )}

                {currentIndex < images.length - 1 && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 bg-white/60 hover:bg-white/90 border border-slate-200 text-slate-800 rounded-full backdrop-blur-md transition-all hidden md:flex hover:scale-105 active:scale-95 shadow-lg"
                        onClick={handleNext}
                    >
                        <ChevronRight className="w-8 h-8" />
                    </Button>
                )}

                {/* Bottom Thumbnails Strip */}
                {images.length > 1 && (
                    <div className="absolute overflow-x-auto overflow-y-hidden bottom-0 inset-x-0 h-28 bg-gradient-to-t from-white/90 to-transparent flex items-center justify-center gap-2 px-6 pb-6 pt-8 scrollbar-hide z-50">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                className={cn(
                                    "relative h-14 w-14 rounded-md overflow-hidden flex-shrink-0 transition-all border-2",
                                    currentIndex === idx 
                                        ? "border-[#0D8DC8] scale-110 shadow-lg z-10 opacity-100" 
                                        : "border-transparent opacity-40 hover:opacity-100 hover:scale-105"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentIndex(idx);
                                    onIndexChange?.(idx);
                                }}
                            >
                                <img src={getUrl(img)} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
